import {
  InteractionDetails,
  InteractionDetailsResponse,
  UnityErrorResponse,
} from '../types/apiResponses/unityResponses';
import { to } from '../utils';
import { fetchInteractionDetails, fetchInteractionStatus } from './api';
import { ColorScheme } from './constants';
import { launchInIframe } from './utils/launchInIframe';
import { hideLoader, showLoader } from './utils/loader';
import ScLoanError, { lasErrorInstanceMap } from './utils/scLoanError';

/**
 * This class contains template/common functionality for las methods,
 * las methods are free to override any functionality by extending the class
 * however the execution of these method are done using the lasRunner util method
 *
 */
class BaseLasStrategy {
  protected interactionToken: string;

  protected interactionDetails: InteractionDetails;

  /** allows callers to cancel an in-flight interaction */
  protected signal?: AbortSignal;

  /**
   * Color scheme supplied by the integrator. Forwarded verbatim to the web app
   * as the `theme` query param. Undefined when the integrator omits it, in
   * which case no `theme` param is appended.
   */
  protected colorScheme?: ColorScheme;

  /** configurable params that controls the flow */
  // can be overriden in constructors
  protected interactionParams = {
    connectFramePath: 'loader',
  };

  constructor(
    interactionToken: string,
    options: { signal?: AbortSignal; colorScheme?: ColorScheme } = {},
  ) {
    this.interactionToken = interactionToken;
    this.signal = options.signal;
    this.colorScheme = options.colorScheme;
  }

  /** throws USER_CANCELLED if the caller has aborted, routing into the cancel flow */
  public throwIfAborted() {
    if (this.signal?.aborted) throw lasErrorInstanceMap.USER_CANCELLED;
  }

  /**
   * Builds the webview launch URL, appending the integrator-supplied color
   * scheme as the `theme` query param. The SDK is a conduit: the value is
   * forwarded verbatim and never resolved here. When no color scheme is
   * supplied, the param is omitted and the web app applies its default.
   */
  protected getLaunchUrl() {
    const { url } = this.interactionDetails;
    return this.colorScheme ? `${url}&theme=${this.colorScheme}` : url;
  }

  async initInteraction() {
    // show loading screen while api resolves
    showLoader();

    const [interactionDetailsError, interactionDetailsResponse] = await to<
      InteractionDetailsResponse,
      UnityErrorResponse
    >(fetchInteractionDetails(this.interactionToken));
    if (interactionDetailsError)
      throw new ScLoanError(
        interactionDetailsError.message,
        interactionDetailsError.code,
      );
    this.interactionDetails = interactionDetailsResponse.data;
  }

  async runInteraction() {
    const clientPayload = await launchInIframe(this.getLaunchUrl(), {
      signal: this.signal,
      colorScheme: this.colorScheme,
    });
    if (clientPayload.code !== 0) {
      throw new ScLoanError(clientPayload.message, clientPayload.code);
    }
  }

  async finishInteraction() {
    // show loading screen while api resolves
    showLoader();

    // get latest interaction status
    const [apiError, statusResponse] = await to(
      fetchInteractionStatus(this.interactionToken),
    );

    // close loader once api resolves
    hideLoader();

    if (apiError) {
      throw lasErrorInstanceMap.INTERNAL_ERROR;
    }
    if (statusResponse.code !== 0)
      throw new ScLoanError(statusResponse.message, statusResponse.code);

    return statusResponse.data;
  }

  async handleInteractionError(err: Error) {
    const { code, message } =
      err instanceof ScLoanError ? err : lasErrorInstanceMap.INTERNAL_ERROR;
    const [apiError, statusResponse] = await to(
      fetchInteractionStatus(this.interactionToken, { code, message }),
    );

    hideLoader();

    if (apiError) {
      throw lasErrorInstanceMap.INTERNAL_ERROR;
    }
    if (statusResponse.code !== 0)
      throw new ScLoanError(
        statusResponse.message,
        statusResponse.code,
        statusResponse.data,
      );

    return statusResponse.data;
  }
}

export default BaseLasStrategy;
